/**
 * Combat State Types
 *
 * Types for managing combat simulation, creature instances, actions, and events.
 * Used by the authoritative server combat engine (60 tps).
 */
/**
 * Type guard for damage events
 * @param event - Combat event to check
 * @returns True if event is a damage event
 */
export function isDamageDealtEvent(event) {
    return event.eventType === 'damage_dealt';
}
/**
 * Type guard for healing events
 * @param event - Combat event to check
 * @returns True if event is a healing event
 */
export function isHealingDoneEvent(event) {
    return event.eventType === 'healing_done';
}
/**
 * Type guard for ability used events
 */
export function isAbilityUsedEvent(event) {
    return event.eventType === 'ability_used';
}
/**
 * Type guard for cleanse events
 */
export function isCleanseAppliedEvent(event) {
    return event.eventType === 'cleanse_applied';
}
/**
 * Check if damage event has execute bonus applied
 * @param data - Damage event data to check
 * @returns True if execute was applied
 */
export function isExecuteDamage(data) {
    return data.isExecute === true;
}
/**
 * Check if healing is from lifesteal
 * @param data - Healing event data to check
 * @returns True if healing is from lifesteal
 */
export function isLifestealHealing(data) {
    return data.healType === 'lifesteal';
}
/**
 * Type guard for shield applied events
 */
export function isShieldAppliedEvent(event) {
    return event.eventType === 'shield_applied';
}
/**
 * Type guard for shield damaged events
 */
export function isShieldDamagedEvent(event) {
    return event.eventType === 'shield_damaged';
}
/**
 * Type guard for shield broken events
 */
export function isShieldBrokenEvent(event) {
    return event.eventType === 'shield_broken';
}
/**
 * Check if a creature has an active shield
 * @param creature - Combat creature to check
 * @returns True if creature has shield HP > 0
 */
export function hasActiveShield(creature) {
    return (creature.currentShield ?? 0) > 0;
}
/**
 * Type guard for reflect triggered events
 */
export function isReflectTriggeredEvent(event) {
    return event.eventType === 'reflect_triggered';
}
/**
 * Check if a creature has an active reflect buff
 * @param creature - Combat creature to check
 * @returns The reflect percentage if active, 0 otherwise
 */
export function getReflectPercent(creature) {
    const reflectBuff = creature.activeBuffs.find(b => b.effects.reflectPercent);
    return reflectBuff?.effects.reflectPercent ?? 0;
}
/**
 * Type guard for attack started events
 * @param event - Combat event to check
 * @returns True if event is an attack started event
 */
export function isAttackStartedEvent(event) {
    return event.eventType === 'attack_started';
}
/**
 * Type guard for projectile launched events
 * @param event - Combat event to check
 * @returns True if event is a projectile launched event
 */
export function isProjectileLaunchedEvent(event) {
    return event.eventType === 'projectile_spawned' && 'projectileIndex' in event.data;
}
/**
 * Type guard for unit moved events
 * @param event - Combat event to check
 * @returns True if event is a unit moved event
 */
export function isUnitMovedEvent(event) {
    return event.eventType === 'unit_moved';
}
/**
 * Type guard for unit waiting events
 * @param event - Combat event to check
 * @returns True if event is a unit waiting event
 */
export function isUnitWaitingEvent(event) {
    return event.eventType === 'unit_waiting';
}
/**
 * Type guard for unit died events
 * @param event - Combat event to check
 * @returns True if event is a unit died event
 */
export function isUnitDiedEvent(event) {
    return event.eventType === 'unit_died';
}
/**
 * Type guard for taunt applied events
 */
export function isTauntAppliedEvent(event) {
    return event.eventType === 'taunt_applied';
}
/**
 * Type guard for charm applied events
 */
export function isCharmAppliedEvent(event) {
    return event.eventType === 'charm_applied';
}
/**
 * Type guard for blind applied events
 */
export function isBlindAppliedEvent(event) {
    return event.eventType === 'blind_applied';
}
/**
 * Type guard for disarm applied events
 */
export function isDisarmAppliedEvent(event) {
    return event.eventType === 'disarm_applied';
}
/**
 * Type guard for silence applied events
 */
export function isSilenceAppliedEvent(event) {
    return event.eventType === 'silence_applied';
}
/**
 * Type guard for attack missed events
 */
export function isAttackMissedEvent(event) {
    return event.eventType === 'attack_missed';
}
/**
 * Type guard for CC replaced events
 */
export function isCCReplacedEvent(event) {
    return event.eventType === 'cc_replaced';
}
/**
 * Type guard for strike telegraph events
 */
export function isStrikeTelegraphEvent(event) {
    return event.eventType === 'strike_telegraph';
}
/**
 * Type guard for strike impact events
 */
export function isStrikeImpactEvent(event) {
    return event.eventType === 'strike_impact';
}
/**
 * Type guard for strike dodged events
 */
export function isStrikeDodgedEvent(event) {
    return event.eventType === 'strike_dodged';
}
/**
 * Type guard for breath started events
 */
export function isBreathStartedEvent(event) {
    return event.eventType === 'breath_started';
}
/**
 * Default combat configuration
 */
export const DEFAULT_COMBAT_CONFIG = {
    tickRate: 22, // Slowed further (was 30) for better readability of visual effects
    maxTicks: 6600, // 5 minutes at 22 TPS
    broadcastRate: 10 // 10 updates per second
};
//# sourceMappingURL=combat.js.map